import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  Text,
  Flex,
  ModalCloseButton,
  Spinner,
  ModalBody,
  Button,
  ModalFooter,
  TabList,
  Tab,
  Tabs,
  TabPanels,
  TabPanel,
  useDisclosure,
  Box,
  FormControl,
  Heading,
  Tooltip,
} from '@chakra-ui/react';
import {
  ContactAddToType,
  SimpleContact,
} from '@texas/api/endpoints/contactsApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { ContactsModal } from '@texas/components/contacts/ContactsModal';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorDetails } from '../alert/ErrorDetails';
import { EmptyDataButton } from '../button/EmptyDataButton';
import { TexasFormLabel } from '../form/TexasFormLabel';
import { Icons } from '../Icons';
import { SimpleNote } from '../SimpleNote';
import { SharedDisclosureProps } from '../types';
import { WarningComponent } from '../WarningComponent';
import { AddBrandTransportRequestModal } from './AddBrandTransportRequestModal';
import { TransportRequestItem } from './TransportRequestItem';
import { request } from '@texas/utils/helpers/httpHelpers';
import { transportRequestsApi } from '@texas/api/endpoints/transportRequestApi';
import { SamplesTable } from '../samples/SamplesTable';
import { UserAvatar } from '../users/UserAvatar';
import { BaseSample } from '@texas/api/endpoints/samplesApi';
import { SimpleUserAvatarButtons } from '../users/SimpleUserAvatarButtons';

interface Props extends SharedDisclosureProps {
  sampleIds: number[];
  onAdded: () => void;
}

interface TransportRequest {
  customerId: number;
  id: number;
  sampleIds: number[];
}
export function AddSamplesToBrandTransportRequestModal(props: Props) {
  const { t } = useTranslation();

  return (
    <Modal isOpen={props.isOpen} onClose={props.onClose} size="6xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex gap={2} align="center">
            <Text>{t('transportRequest.addSamples')}</Text>
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        <Inner
          sampleIds={props.sampleIds}
          onAdd={() => {
            props.onAdded();
            props.onClose();
          }}
        />
      </ModalContent>
    </Modal>
  );
}

function Inner({
  sampleIds,
  onAdd,
}: {
  sampleIds: number[];
  onAdd: () => void;
}) {
  const { t } = useTranslation();

  const {
    data: customers,
    refetch: refetchSamples,
    loading: samplesLoading,
    error,
  } = useApiResource(transportRequestsApi.toBrandFromSampleIds);

  useEffect(() => {
    refetchSamples(sampleIds);
  }, [refetchSamples, sampleIds]);

  const [transportRequests, setTransportRequests] = useState<
    TransportRequest[]
  >([]);

  const {
    request: addSamplesRequest,
    loading: requestLoading,
    error: requestError,
  } = useApiRequest(transportRequestsApi.addSamplesToMultipleTransportRequests);

  const performRequest = () => {
    request(
      addSamplesRequest,
      [
        {
          data: transportRequests.map((x) => ({
            sampleIds: x.sampleIds,
            transportRequestId: x.id,
          })),
        },
      ],
      () => {
        onAdd();
      },
    );
  };

  return (
    <>
      {requestError && <ErrorDetails error={requestError} />}
      <ModalBody>
        {error && <ErrorDetails error={error} />}
        {samplesLoading && <Spinner />}

        <Tabs>
          <TabList>
            {customers?.map((c) => {
              return <Tab key={c.customerId}>{c.customerName}</Tab>;
            })}
          </TabList>
          <TabPanels>
            {customers?.map((c) => {
              return (
                <TabPanel key={c.customerId}>
                  <Flex gap={4} alignItems="center">
                    <Flex flexDir="column" gap={2} w="60%">
                      <SamplesTable
                        samples={c.samples}
                        maxH="600px"
                        overflowY="auto"
                      />
                    </Flex>
                    <Icons.ArrowRight opacity={0.2} boxSize={12} />
                    <TransportsList
                      defaultContact={c.contact}
                      customerId={c.customerId}
                      samples={c.samples}
                      setTransportRequests={setTransportRequests}
                      selectedId={
                        transportRequests.find(
                          (x) => x.customerId === c.customerId,
                        )?.id ?? null
                      }
                    />
                  </Flex>
                </TabPanel>
              );
            })}
          </TabPanels>
        </Tabs>
      </ModalBody>
      <ModalFooter>
        <Tooltip
          label={
            transportRequests.length !== customers?.length
              ? t('transportRequest.handleAllSamples')
              : ''
          }
        >
          <Button
            isDisabled={
              transportRequests.length !== customers?.length || requestLoading
            }
            isLoading={requestLoading}
            onClick={performRequest}
          >
            {t('general.save')}
          </Button>
        </Tooltip>
      </ModalFooter>
    </>
  );
}

function TransportsList({
  selectedId,
  setTransportRequests,
  defaultContact,
  customerId,
  samples,
}: {
  selectedId: number | null;
  setTransportRequests: Dispatch<SetStateAction<TransportRequest[]>>;
  defaultContact: SimpleContact | null;
  customerId: number;
  samples: BaseSample[];
}) {
  const { isOpen, onClose, onOpen } = useDisclosure();
  const {
    isOpen: isCustomerAddContactOpen,
    onClose: onCustomerAddContactClose,
    onOpen: onCustomerAddContactOpen,
  } = useDisclosure();
  const { t } = useTranslation();

  const getContact = useCallback(() => {
    const first = samples[0].productApproval.contact;
    if (samples.every((x) => x.productApproval.contact.id === first.id)) {
      return first;
    }
    return defaultContact;
  }, [defaultContact, samples]);

  const [contact, setContact] = useState<SimpleContact | null>(getContact());

  const {
    data: upcoming,
    refetch: refetchUpcoming,
    loading: upcomingLoading,
    set: setUpcoming,
  } = useApiResource(transportRequestsApi.upcomingFromMeToContact);

  useEffect(() => {
    if (!contact) {
      setUpcoming([]);
      return;
    }
    refetchUpcoming(contact.id);
  }, [refetchUpcoming, contact, setUpcoming]);

  const setTransportRequestCallback = useCallback(
    (id: number | null) => {
      if (id === null) {
        setTransportRequests((t) =>
          t.filter((x) => x.customerId !== customerId),
        );
        return;
      } else {
        setTransportRequests((t) => {
          if (t.find((x) => x.customerId === customerId) === undefined) {
            return [
              ...t,
              {
                customerId: customerId,
                id: id,
                sampleIds: samples.map((sa) => sa.sampleId),
              },
            ];
          }

          return t.map((x) => {
            if (x.customerId === customerId) {
              return { ...x, id };
            }
            return x;
          });
        });
      }
    },
    [setTransportRequests, customerId, samples],
  );

  useEffect(() => {
    if (upcoming !== null && upcoming.length > 0) {
      setTransportRequestCallback(upcoming[0].id);
    }
  }, [setTransportRequestCallback, upcoming]);

  const { request: removeReq, ...removeRes } = useApiRequest(
    transportRequestsApi.remove,
  );

  const performRemove = (id: number) =>
    request(removeReq, [id], () => {
      if (contact) {
        refetchUpcoming(contact.id);
      }
    });

  return (
    <>
      <Flex flexDir="column" gap={2} w="40%">
        <Heading fontSize="md">{t('transportRequest.select')}</Heading>
        <FormControl>
          <TexasFormLabel>
            {t('transportRequest.whoWillRecieve')}
          </TexasFormLabel>
          <Box>
            {contact === null ? (
              <Box maxH={32}>
                <WarningComponent
                  mt={2}
                  warning={t('productApproval.brandContactRequired')}
                >
                  <EmptyDataButton
                    label={t('productApproval.selectBrandContact')}
                    description={t('contacts.select')}
                    onClick={onCustomerAddContactOpen}
                  />
                </WarningComponent>
              </Box>
            ) : (
              <>
                <UserAvatar
                  name={contact.name}
                  email={contact.email}
                  label={null}
                  showTeams={false}
                  buttons={
                    <SimpleUserAvatarButtons
                      buttons={[
                        {
                          tooltip: t('transportRequest.changeReceiver'),
                          icon: <Icons.Pencil />,
                          onClick: onCustomerAddContactOpen,
                        },
                      ]}
                    />
                  }
                />
              </>
            )}
          </Box>
        </FormControl>
        {contact ? (
          <>
            {upcomingLoading || removeRes.loading ? (
              <Flex gap={2}>
                <Spinner />
                <Text>{t('general.loadingData')}</Text>
              </Flex>
            ) : (
              <>
                {upcoming?.map((x) => (
                  <TransportRequestItem
                    onClick={() => setTransportRequestCallback(x.id)}
                    isSelected={selectedId === x.id}
                    key={x.id}
                    transport={x}
                    onRemove={
                      x.samplesInTransport === 0
                        ? () => performRemove(x.id)
                        : undefined
                    }
                  />
                ))}
                <EmptyDataButton
                  label={t('transportRequest.createTicket')}
                  description={t('transportRequest.request')}
                  onClick={onOpen}
                />
              </>
            )}
          </>
        ) : (
          <SimpleNote>{t('transportRequest.selectToSeeUpcoming')}</SimpleNote>
        )}
      </Flex>
      {contact && (
        <AddBrandTransportRequestModal
          isOpen={isOpen}
          onClose={onClose}
          onCreate={(id) => {
            setTransportRequestCallback(id);
            refetchUpcoming(contact.id);
          }}
          contact={contact}
        />
      )}
      <ContactsModal
        mode="select"
        filterOn={ContactAddToType.Customer}
        addTo={ContactAddToType.Customer}
        referenceId={customerId}
        onClose={() => {
          onCustomerAddContactClose();
        }}
        isOpen={isCustomerAddContactOpen}
        onCreatedAndConnected={(c) => {
          setContact(c);
          setTransportRequestCallback(null);
          onCustomerAddContactClose();
        }}
        onSelect={(c) => {
          setContact(c);
          setTransportRequestCallback(null);
          onCustomerAddContactClose();
        }}
      />
    </>
  );
}
